import {ReactNode, SelectHTMLAttributes, useId} from 'react';
import {ChevronDown, Pencil, Trash2} from 'lucide-react';
import {StatusBadge} from 'ch3-ui-lib';
import type {
    CallRestrictionRule,
    DestinationCategory,
    RuleAction,
    ScopeMode,
} from '../../store/features/CallRestrictions/callRestrictionsTypes';
import {appliesToText, describeDestination, MODE_LABEL} from '../../utils/callRestrictions/destinations';
import {ruleName} from './scopes';

export const lucide = '!p-0 shrink-0 !text-current';

interface CardProps {
    title: ReactNode;
    description?: ReactNode;
    /** Controls on the right of the title, e.g. the list type switch. */
    actions?: ReactNode;
    children?: ReactNode;
    testId?: string;
}

/** White DS card with a 16px title and a footnote description. */
export const Card = ({title, description, actions, children, testId}: CardProps) => {
    const id = useId();
    return (
        <section aria-labelledby={id} data-testid={testId} className="flex flex-col gap-3 rounded-card bg-surface p-4">
            <div className="flex flex-wrap items-center gap-3">
                <div className="min-w-[240px] flex-1">
                    <h2 id={id} className="m-0 flex items-center gap-2 text-body-strong text-ink">{title}</h2>
                    {description && <p className="m-0 mt-0.5 text-footnote text-ink-muted">{description}</p>}
                </div>
                {actions}
            </div>
            {children}
        </section>
    );
};

export const RuleBadge = ({action}: {action: RuleAction}) => (
    <StatusBadge tone={action === 'BLOCK' ? 'danger' : 'success'} className="!h-[22px] !text-caption">
        {action === 'BLOCK' ? 'Block' : 'Allow'}
    </StatusBadge>
);

export const ModeBadge = ({mode}: {mode: ScopeMode | null}) => (
    <StatusBadge tone={mode === 'ALLOW_LIST' ? 'success' : mode === 'BLOCK_LIST' ? 'danger' : 'brand'} className="!h-[22px] !text-caption">
        {mode ? MODE_LABEL[mode] : 'Exception'}
    </StatusBadge>
);

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
    label: string;
    children: ReactNode;
}

/** Native select in the DS input shape (36px, radius 10). */
export const LabeledSelect = ({label, children, className = '', id, ...props}: SelectProps) => {
    const generated = useId();
    const selectId = id ?? generated;
    return (
        <div className={`flex flex-col gap-1.5 ${className}`}>
            <label htmlFor={selectId} className="text-footnote font-medium text-ink-secondary">{label}</label>
            <div className="relative">
                <select
                    id={selectId}
                    className="h-control-md w-full cursor-pointer appearance-none rounded-control border border-solid border-fill-subtle bg-surface pl-3 pr-9 text-callout text-ink outline-none focus:border-transparent focus:ring-2 focus:ring-brand-focus disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-ink-disabled"
                    {...props}
                >
                    {children}
                </select>
                <ChevronDown size={16} aria-hidden="true" className={`${lucide} pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted`}/>
            </div>
        </div>
    );
};

const GRID = 'grid grid-cols-[minmax(220px,1.4fr)_92px_minmax(150px,1fr)_minmax(160px,1fr)_72px] gap-3';

interface RulesTableProps {
    rules: CallRestrictionRule[];
    categories: DestinationCategory[];
    readOnly: boolean;
    onEdit: (rule: CallRestrictionRule) => void;
    onDelete: (rule: CallRestrictionRule) => void;
    emptyText: string;
}

/** Destination · Rule · Applies to · Note, with edit and delete per row. */
export const RulesTable = ({rules, categories, readOnly, onEdit, onDelete, emptyText}: RulesTableProps) => {
    if (rules.length === 0) {
        return <p className="m-0 rounded-control bg-surface-muted px-3 py-3 text-footnote text-ink-muted">{emptyText}</p>;
    }
    return (
        <div className="overflow-x-auto">
            <div role="table" aria-label="Rules" className="min-w-[700px]">
                <div role="row" className={`${GRID} border-0 border-b border-solid border-line px-2 pb-2 text-caption text-ink-muted`}>
                    <span role="columnheader">Destination</span>
                    <span role="columnheader">Rule</span>
                    <span role="columnheader">Applies to</span>
                    <span role="columnheader">Note</span>
                    <span role="columnheader"><span className="sr-only">Actions</span></span>
                </div>
                {rules.map((rule) => {
                    const text = describeDestination(rule.destination, categories, ruleName(rule));
                    return (
                        <div role="row" key={rule.id} data-testid="call-restriction-rule" className={`${GRID} min-h-11 items-center border-0 border-b border-solid border-surface-muted px-2 py-1.5`}>
                            <div role="cell" className="min-w-0">
                                <div className="truncate text-callout text-ink">{text.name}</div>
                                <div className="truncate text-footnote text-ink-muted">{text.detail}</div>
                            </div>
                            <span role="cell"><RuleBadge action={rule.action}/></span>
                            <span role="cell" className="text-footnote text-ink-secondary">{appliesToText(rule.appliesTo)}</span>
                            <span role="cell" className="truncate text-footnote text-ink-muted" title={rule.note ?? undefined}>{rule.note}</span>
                            <span role="cell" className="flex justify-end gap-1">
                                {!readOnly && (
                                    <>
                                        <button type="button" aria-label={`Edit rule ${text.name}`} onClick={() => onEdit(rule)}
                                                className="flex size-7 cursor-pointer items-center justify-center rounded-chip text-ink-secondary hover:bg-surface-muted">
                                            <Pencil size={16} aria-hidden="true" className={lucide}/>
                                        </button>
                                        <button type="button" aria-label={`Delete rule ${text.name}`} onClick={() => onDelete(rule)}
                                                className="flex size-7 cursor-pointer items-center justify-center rounded-chip text-ink-secondary hover:bg-surface-muted">
                                            <Trash2 size={16} aria-hidden="true" className={lucide}/>
                                        </button>
                                    </>
                                )}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
